import React, { useEffect, useState } from 'react';
import { 
  MapPin, 
  Phone, 
  MessageCircle, 
  Building, 
  FileText,
  CheckCircle
} from 'lucide-react';
import PropertyCard from './PropertyCardBroker';
import RequirementCard from './RequirementCardBroker';
import Cookies from 'js-cookie';
import { useParams } from 'react-router-dom';
import apiClient from '../common/apiClient';

const BrokerProfile = () => {
  // State management for active tab and data
  const [activeTab, setActiveTab] = useState('properties'); // Tracks which tab is active
  const [profileData, setProfileData] = useState(null); // Stores broker profile information
  const [properties, setProperties] = useState([]); // Stores broker's property listings
  const [requirements, setRequirements] = useState([]); // Stores broker's requirement listings
  const [loading, setLoading] = useState(true); // Tracks loading state for API calls
  const [showNumber, setShowNumber] = useState(false); // State to toggle phone number visibility
  
  // Get brokerId from URL parameters if provided in route
  const { bid } = useParams();
  
  // Get current user ID from browser cookies for authenticated users
  const currentUserId = Cookies.get("userId");
  
  // Determine which ID to use: URL parameter takes priority, fallback to cookies
  const finalBrokerId = bid || currentUserId;
  
  // API endpoint for broker details
  const API_ENDPOINT = `/cjidnvij/ceksfbuebijn/user/getBrokerDetails/${finalBrokerId}`;
  // console.log("Fetching broker details for ID:", finalBrokerId);


  // Fetch broker profile data when component mounts or ID changes
  useEffect(() => {
    const fetchProfileData = async () => {
      // Early return if no broker ID is available
      if (!finalBrokerId) {
        // console.error('No broker ID found');
        setLoading(false);
        return;
      }

      try {
        // API call to fetch broker details using apiClient
        // 👇 Add cache-busting param to avoid 304 and caching
const response = await apiClient("get", `${API_ENDPOINT}?_=${Date.now()}`);
// console.log('API Response:', response.data);

if (response.data?.success) {
  const { broker = [], brokerProperties = [], brokerRequirements = [] } = response.data.data;

  // API returns `broker` as an array → take first item.
  // If someday it returns an object, this also handles that case.
  const brokerInfo =
    Array.isArray(broker)
      ? (broker.length > 0 ? broker[0] : null)
      : (broker && typeof broker === 'object' ? broker : null);

  // console.log("Setting profileData to:", brokerInfo);

  setProfileData(brokerInfo);
  setProperties(brokerProperties);
  setRequirements(brokerRequirements);
}


// console.log("Fetched Broker:", response.data.data.broker);


      } catch (error) {
        // console.error('Error fetching profile:', error);
      } finally {
        setLoading(false); // Ensure loading state is turned off
      }
    };

    fetchProfileData();
  }, [finalBrokerId, API_ENDPOINT]); // Re-run effect when broker ID or API endpoint changes

  /**
   * Transforms raw property data from API into standardized format for PropertyCard component
   * @param {Object} property - Raw property data from API
   * @returns {Object} Formatted property data with consistent structure
   */
  const getPropertyData = (property) => {
    return {
      id: property.propertyId || property._id || 'N/A',
      date: property.listedDate ? new Date(property.listedDate).toLocaleDateString('en-GB') : 'N/A',
      premise: property.title || 'No Title',
      rent: parseInt(property.price) || 0,
      status: property.propertyCurrentStatus || 'Unknown',
      type: property.type || 'Unknown Type',
      subtype: property.unitType || 'Unknown',
      area: property.area || 'Unknown Area',
      size: property.bhk && property.bhk.length > 0 && property.bhk[0] ? property.bhk[0] : (property.unitType || 'Unknown Size'),
      sqft: property.sqFt || 0,
      condition: property.furnishedType || 'Unknown',
      address: property.address || 'No Address',
      description: property.description || 'No description available',
      contactName: property.name || 'No Name',
      contactPhone: property.number || 'No Phone',
      imageUrl: property.media && property.media.length > 0 ? property.media[0].url : ''
    };
  };

  /**
   * Transforms raw requirement data from API into standardized format for RequirementCard component
   * @param {Object} requirement - Raw requirement data from API
   * @returns {Object} Formatted requirement data with consistent structure
   */
  const getRequirementData = (requirement) => {
    // Extract price range with fallback values
    const rentMin = requirement.priceRange?.min || 0;
    const rentMax = requirement.priceRange?.max || rentMin;

    // Initialize size range with default values
    let sizeMin = "1 BHK";
    let sizeMax = "3 BHK";

    // Construct size range object with proper fallbacks
    const sizeRange = {
      sqFt: {
        min: requirement.sizeRange?.sqFt?.min || 0,
        max: requirement.sizeRange?.sqFt?.max || 0,
      },
      sqYd: {
        min: requirement.sizeRange?.sqYd?.min || 0,
        max: requirement.sizeRange?.sqYd?.max || 0,
      },
    };

    // Determine size range based on available data
    if (requirement.bhk && requirement.bhk.length > 0) {
      // Use BHK values if available
      const sortedBhk = [...requirement.bhk].sort();
      sizeMin = sortedBhk[0];
      sizeMax = sortedBhk[sortedBhk.length - 1];
    } else if (requirement.sizeRange?.sqFt?.min) {
      // Convert square footage to approximate BHK if available
      const minSqFt = requirement.sizeRange.sqFt.min;
      const maxSqFt = requirement.sizeRange.sqFt.max || minSqFt;
      sizeMin = getBhkFromSqFt(minSqFt);
      sizeMax = getBhkFromSqFt(maxSqFt);
    }

    return {
      id: requirement.requirementId || requirement._id || "REQ" + Math.random().toString(36).substr(2, 9),
      date: requirement.listedDate ? new Date(requirement.listedDate).toLocaleDateString("en-GB") : new Date().toLocaleDateString("en-GB"),
      type: requirement.type || "Residential Rent",
      rentMin: rentMin,
      rentMax: rentMax,
      sizeMin: sizeMin,
      sizeMax: sizeMax,
      sizeRange, // Include detailed size range for display
      areas: requirement.areas || ["Unknown Area"],
      condition: requirement.furnishingPreferences && requirement.furnishingPreferences.length > 0 ? requirement.furnishingPreferences[0] : "Any",
      status: requirement.requirementStatus || "Active",
      description: requirement.description || "No description available",
      preferences: requirement.furnishingPreferences ? requirement.furnishingPreferences.join(", ") : "No preferences specified",
      contactName: requirement.brokerName || "No Name",
      contactPhone: requirement.contactNumber || "No Phone",
      title: requirement.title || requirement.premiseName || "N/A",
    };
  };

  /**
   * Converts square footage to approximate BHK configuration
   * @param {number} sqft - Area in square feet
   * @returns {string} Approximate BHK configuration
   */
  const getBhkFromSqFt = (sqft) => {
    if (sqft >= 2000) return '4+ BHK';
    if (sqft >= 1500) return '3 BHK';
    if (sqft >= 1000) return '2 BHK';
    if (sqft >= 600) return '1 BHK';
    return 'Studio';
  };

  // Loading state display
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7FF] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#503196] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Error state when no broker ID is available
  if (!finalBrokerId) {
    return (
      <div className="min-h-screen bg-[#FAF7FF] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Broker Not Found
          </h3>
          <p className="text-gray-600">
            Please provide a valid broker ID or login to view your profile.
          </p>
        </div>
      </div>
    );
  }

  // Main component render
  return (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundColor: '#FAF7FF' // Light purple background color
      }}
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* Profile Header Section - Displays broker information */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl overflow-hidden mb-8">
          <div className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {/* Profile Picture with Verification Badge */}
              <div className="flex-shrink-0">
                <div className="relative ml-11">
                  <img
                    src={profileData?.profileImage || "https://via.placeholder.com/150"}
                    alt={profileData?.name || "Profile"}
                    className="w-20 h-20 md:w-36 md:h-36 rounded-full border-4 border-white object-cover"
                  />
                  {/* Verification badge */}
                  <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>

              {/* Broker Information Section */}
              <div className="flex-1 min-w-0 ml-10">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    
                    
                    {/* Company Name */}
                    <p className="text-xl md:text-2xl flex  font-bold text-gray-900 mb-1">
                      <Building className="w-4 h-4 mt-2 " />
                      {profileData?.companyName || "No Company"}
                    </p>

                    {/* Broker Name */}
                    <h2 className="text-gray-600 flex items-center gap-2 mb-3">
                      {profileData?.name || "No Name"}
                    </h2>
                  
                  
                    
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
  <div className="flex items-center gap-2 text-sm text-gray-600">
    <MapPin className="w-4 h-4 text-blue-600" />
    <span>{profileData?.address || "No Address"}</span>
  </div>
  
  <div className="flex justify-end items-center gap-4">
    {/* Get Contact Button */}
    {showNumber ? (
      <div className="flex items-center gap-2">
        <button 
          onClick={() => window.open(`tel:${profileData?.number}`)}
          className="flex items-center gap-1 p-2 bg-[#503196] text-white rounded-lg hover:bg-[#503196] transition-colors"
          disabled={!profileData?.number}
        >
          <Phone className="w-4 h-4" />
          <span className="text-xs">{profileData?.number || "No Phone"}</span>
        </button>
      </div>
    ) : (
      <button 
        onClick={() => setShowNumber(true)}
        className="flex items-center gap-1 p-2 bg-[#503196] text-white rounded-lg hover:bg-[#503196] transition-colors"
      >
        <Phone className="w-4 h-4" />
        <span className="text-xs">Get Contact</span>
      </button>
    )}
    
    {/* WhatsApp Button */}
    <button 
      onClick={() => {
        if (profileData?.number) {
          const whatsappUrl = `https://wa.me/${profileData.number.replace(/\D/g, '')}`;
          window.open(whatsappUrl, '_blank');
        }
      }}
      className="flex items-center gap-1 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
      disabled={!profileData?.number}
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893c0-3.18-1.24-6.169-3.495-8.418"/>
</svg>
      <span className="text-xs">WhatsApp</span>
    </button>
  </div>
</div>

                    {/* Statistics Section */}
                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span className="font-bold text-lg text-black">{properties.length}</span>
                        <span className="text-gray-600 ml-1">Properties</span>
                      </div>
                      <div>
                        <span className="font-bold text-lg text-black">{requirements.length}</span>
                        <span className="text-gray-600 ml-1">Requirements</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Properties and Requirements Tab Section */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <div className="flex">
              {/* Properties Tab Button */}
              <button
                onClick={() => setActiveTab('properties')}
                className={`flex-1 px-6 py-4 font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'properties'
                    ? 'bg-[#503196] text-white'
                    : 'bg-white text-black'
                }`}
              >
                {/* <Building className="w-5 h-5" /> */}
                Properties 
              </button>
              
              {/* Requirements Tab Button */}
              <button
                onClick={() => setActiveTab('requirements')}
                className={`flex-1 px-6 py-4 font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'requirements'
                    ? 'bg-[#503196] text-white'
                    : 'bg-white text-black'
                }`}
              >
                {/* <FileText className="w-5 h-5" /> */}
                Requirements 
              </button>
            </div>
          </div>

          {/* Tab Content Area */}
          <div className="p-2">
            {activeTab === 'properties' ? (
              // Properties Tab Content
              <div className="grid grid-cols-1 gap-6">
                {properties.length > 0 ? (
                  // Display properties list if available
                  properties.map((property, index) => (
                    <PropertyCard
                      key={property._id || property.propertyId || `property-${index}`}
                      {...getPropertyData(property)}
                    />
                  ))
                ) : (
                  // Empty state for no properties
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Building className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No properties found
                    </h3>
                    <p className="text-gray-600">
                      Properties will appear here once they are listed.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Requirements Tab Content
              <div className="grid grid-cols-1 gap-6">
                {requirements.length > 0 ? (
                  // Display requirements list if available
                  requirements.map((requirement, index) => (
                    <RequirementCard
                      key={requirement._id || requirement.requirementId || `requirement-${index}`}
                      {...getRequirementData(requirement)}
                    />
                  ))
                ) : (
                  // Empty state for no requirements
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No requirements found
                    </h3>
                    <p className="text-gray-600">
                      Requirements will appear here once they are posted.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerProfile;
